<script setup>
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import { sheetKeyAction, readTsv, writeTsv } from './dataSheetKeys.js'

// RED-317 PR 3d-A — a small spreadsheet for data that lives in a Word
// object: a chart's sheet, an Excel object's cells (PR 3d-B). Controlled:
// the parent owns every value; this keeps only the cursor, the range and the
// one cell being typed into, and reports what the admin typed:
//   draft     { row, col, text } while typing (null when it stops), for a live preview
//   input     [{ row, col, text }] committed texts — Enter/Tab/blur, a paste, Delete over a range
//   header    { col, text } a column header retyped
//   select    { row, col } the active cell moved
//   undo/redo the editor's history (Mod+Z / Mod+Shift+Z / Mod+Y)
// The parent parses each text, keeps what is valid and hands invalid text
// back on the cell (`invalid`), where it stays, flagged, until fixed.
//
// Keys: arrows (Shift extends the range, Mod jumps to the edge), Tab /
// Shift-Tab, Enter / Shift-Enter, F2 or typing to edit, Esc cancels typing,
// Delete/Backspace clear the range, Mod+C / Mod+X / Mod+V copy, cut and paste
// tab-separated text (Excel, Sheets), Mod+A selects all.

const props = defineProps({
  // [{ key, header, headerEditable, kind: 'text'|'number'|'date', leading, readonly, label }]
  columns: { type: Array, required: true },
  // [{ key, label, header?, cells: [{ display, edit, invalid?, group?: 'start'|'cont', readonly?, numeric?, title? }] }]
  // header: the row's own number (an Excel object's row); numeric: right-align this cell (else the column's kind decides)
  rows: { type: Array, required: true },
  label: { type: String, required: true },
  // Accessible label of a cell: (row, col) => string.
  cellLabel: { type: Function, default: null },
  // Rows shown before the sheet scrolls.
  visibleRows: { type: Number, default: 10 },
})

const emit = defineEmits(['draft', 'input', 'header', 'select', 'undo', 'redo'])

const id = `studio-sheet-${Math.random().toString(36).slice(2, 9)}`
const active = ref({ row: 0, col: firstDataColumn() })
const anchor = ref({ ...active.value })
const editing = ref(null) // { row, col, text, original } — row -1 = the header
const grid = useTemplateRef('grid')
// Refs inside v-for are arrays: the one field being typed into, if any.
const inputs = useTemplateRef('input')
const inputField = () => (Array.isArray(inputs.value) ? inputs.value[0] : inputs.value) ?? null
let dragging = false

function firstDataColumn() {
  const index = props.columns.findIndex((column) => !column.leading)
  return index >= 0 ? index : 0
}

const rowCount = computed(() => props.rows.length)
const colCount = computed(() => props.columns.length)

watch([rowCount, colCount], () => {
  active.value = clampCell(active.value)
  anchor.value = clampCell(anchor.value)
})

function clampCell({ row, col }) {
  return { row: Math.max(-1, Math.min(row, rowCount.value - 1)), col: Math.max(0, Math.min(col, colCount.value - 1)) }
}

const range = computed(() => ({
  top: Math.max(0, Math.min(active.value.row, anchor.value.row)),
  bottom: Math.max(active.value.row, anchor.value.row),
  left: Math.min(active.value.col, anchor.value.col),
  right: Math.max(active.value.col, anchor.value.col),
}))

const inRange = (row, col) => row >= range.value.top && row <= range.value.bottom && col >= range.value.left && col <= range.value.right && !(active.value.row === row && active.value.col === col)

const cellId = (row, col) => `${id}-${row}-${col}`
const activeId = computed(() => cellId(active.value.row, active.value.col))

const isEditable = (row, col) => {
  const column = props.columns[col]
  if (!column) return false
  if (row === -1) return Boolean(column.headerEditable)
  return !column.readonly && !props.rows[row]?.cells[col]?.readonly
}

function move(row, col, extend = false) {
  active.value = clampCell({ row, col })
  if (!extend) anchor.value = { ...active.value }
  emit('select', { ...active.value })
  nextTick(() => document.getElementById(activeId.value)?.scrollIntoView({ block: 'nearest', inline: 'nearest' }))
}

function startEditing(text = null) {
  const { row, col } = active.value
  if (!isEditable(row, col)) return
  const original = row === -1 ? props.columns[col].header : (props.rows[row].cells[col].invalid ?? props.rows[row].cells[col].edit ?? '')
  editing.value = { row, col, text: text ?? original, original }
  emit('draft', row === -1 ? null : { row, col, text: editing.value.text })
  nextTick(() => {
    const field = inputField()
    field?.focus()
    field?.setSelectionRange(field.value.length, field.value.length)
  })
}

function finishEditing(commit) {
  const current = editing.value
  if (!current) return
  editing.value = null
  emit('draft', null)
  if (commit && current.text !== current.original) {
    if (current.row === -1) emit('header', { col: current.col, text: current.text })
    else emit('input', [{ row: current.row, col: current.col, text: current.text }])
  }
  grid.value?.focus({ preventScroll: true })
}

function onInput(event) {
  editing.value.text = event.target.value
  if (editing.value.row >= 0) emit('draft', { row: editing.value.row, col: editing.value.col, text: editing.value.text })
}

function onEditKeydown(event) {
  const action = sheetKeyAction(event, true)
  if (!action) return
  event.preventDefault()
  event.stopPropagation()
  if (action.type === 'cancel') return finishEditing(false)
  finishEditing(true)
  if (action.type === 'move') step(action.dRow, action.dCol, action.wrap)
}

function step(dRow, dCol, wrap = false) {
  let { row, col } = active.value
  row += dRow
  col += dCol
  if (wrap && col >= colCount.value) {
    col = 0
    row += 1
  } else if (wrap && col < 0) {
    col = colCount.value - 1
    row -= 1
  }
  move(Math.min(row, rowCount.value - 1), col)
}

function onKeydown(event) {
  if (editing.value) return
  const action = sheetKeyAction(event, false)
  if (!action) return
  event.preventDefault()
  switch (action.type) {
    case 'move':
      step(action.dRow, action.dCol, action.wrap)
      break
    case 'extend':
      move(active.value.row + action.dRow, active.value.col + action.dCol, true)
      break
    case 'edge':
      move(action.dRow < 0 ? 0 : action.dRow > 0 ? rowCount.value - 1 : active.value.row, action.dCol < 0 ? 0 : action.dCol > 0 ? colCount.value - 1 : active.value.col, action.extend)
      break
    case 'edit':
      startEditing(null)
      break
    case 'type':
      startEditing(action.text)
      break
    case 'clear':
      clearRange()
      break
    case 'copy':
    case 'cut':
      copyRange(action.type === 'cut')
      break
    case 'all':
      anchor.value = { row: 0, col: 0 }
      active.value = { row: rowCount.value - 1, col: colCount.value - 1 }
      break
    case 'undo':
    case 'redo':
      emit(action.type)
      break
  }
}

function rangeCells() {
  const cells = []
  for (let row = range.value.top; row <= range.value.bottom; row++) {
    for (let col = range.value.left; col <= range.value.right; col++) if (row >= 0 && isEditable(row, col)) cells.push({ row, col })
  }
  return cells
}

function clearRange() {
  const cells = rangeCells().filter(({ row, col }) => (props.rows[row].cells[col].invalid ?? props.rows[row].cells[col].edit ?? '') !== '')
  if (cells.length) emit('input', cells.map((cell) => ({ ...cell, text: '' })))
}

async function copyRange(cut) {
  const lines = []
  for (let row = range.value.top; row <= range.value.bottom; row++) {
    const values = []
    for (let col = range.value.left; col <= range.value.right; col++) {
      const cell = row === -1 ? { edit: props.columns[col].header } : props.rows[row].cells[col]
      values.push(cell.invalid ?? cell.edit ?? '')
    }
    lines.push(values)
  }
  try {
    await navigator.clipboard?.writeText(writeTsv(lines))
  } catch {
    // The clipboard may be refused (permissions); nothing else to do.
  }
  if (cut) clearRange()
}

function onPaste(event) {
  if (editing.value) return
  const text = event.clipboardData?.getData('text/plain')
  if (!text) return
  event.preventDefault()
  const block = readTsv(text)
  const cells = []
  block.forEach((values, rowOffset) => {
    values.forEach((value, colOffset) => {
      const row = active.value.row + rowOffset
      const col = active.value.col + colOffset
      if (col >= colCount.value || row < 0) return
      if (row < rowCount.value && !isEditable(row, col)) return
      cells.push({ row, col, text: value })
    })
  })
  if (!cells.length) return
  emit('input', cells)
  anchor.value = { ...active.value }
  active.value = { row: Math.min(active.value.row + block.length - 1, Math.max(rowCount.value - 1, active.value.row + block.length - 1)), col: Math.min(active.value.col + Math.max(...block.map((values) => values.length)) - 1, colCount.value - 1) }
  // A block the chart could not take added no rows: the selection stays inside the sheet.
  nextTick(() => {
    active.value = clampCell(active.value)
    anchor.value = clampCell(anchor.value)
  })
}

function onCellMousedown(event, row, col) {
  if (editing.value && (editing.value.row !== row || editing.value.col !== col)) finishEditing(true)
  if (event.button !== 0) return
  event.preventDefault()
  move(row, col, event.shiftKey)
  dragging = true
  grid.value?.focus({ preventScroll: true })
}

function onCellMouseenter(row, col) {
  if (dragging) move(row, col, true)
}

function onMouseup() {
  dragging = false
}

function onCellDblclick(row, col) {
  move(row, col)
  startEditing(null)
}

/** Focus the sheet on a cell (the parent: after "Valmis" found invalid cells). */
function focusCell(row, col) {
  move(row, col)
  grid.value?.focus({ preventScroll: true })
}

defineExpose({ focusCell, focus: () => grid.value?.focus({ preventScroll: true }) })

const isNumeric = (col) => props.columns[col]?.kind === 'number'
const isEditingCell = (row, col) => editing.value?.row === row && editing.value?.col === col
const ariaLabelOf = (row, col) => props.cellLabel?.(row, col) ?? `${props.columns[col]?.label ?? ''} ${props.rows[row]?.label ?? ''}`.trim()
const maxHeight = computed(() => `${(props.visibleRows + 1) * 1.75}rem`)
</script>

<template>
  <div
    :id="id"
    ref="grid"
    class="studio-sheet relative overflow-auto rounded-md border border-gray-200 bg-white text-[13px] leading-tight text-gray-800 outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
    :style="{ maxHeight }"
    role="grid"
    tabindex="0"
    :aria-label="label"
    :aria-rowcount="rowCount + 1"
    :aria-colcount="colCount + 1"
    :aria-activedescendant="editing ? undefined : activeId"
    data-testid="studio-sheet"
    @keydown="onKeydown"
    @paste="onPaste"
    @mouseup="onMouseup"
    @mouseleave="onMouseup"
  >
    <table class="w-full border-separate border-spacing-0">
      <thead>
        <tr role="row" aria-rowindex="1">
          <th class="sticky top-0 left-0 z-30 w-9 border-b border-r border-gray-200 bg-gray-50" aria-hidden="true" />
          <th
            v-for="(column, col) in columns"
            :id="cellId(-1, col)"
            :key="column.key"
            role="columnheader"
            :aria-colindex="col + 2"
            :aria-selected="active.row === -1 && active.col === col"
            class="studio-sheet-cell sticky top-0 z-20 h-7 min-w-24 border-b border-r border-gray-200 bg-gray-50 px-2 text-left font-medium whitespace-nowrap text-gray-600"
            :class="{ 'left-9 z-30': column.leading && col === 0, 'is-active': active.row === -1 && active.col === col, 'cursor-text': column.headerEditable }"
            :data-testid="`studio-sheet-header-${col}`"
            @mousedown="onCellMousedown($event, -1, col)"
            @dblclick="onCellDblclick(-1, col)"
          >
            <input
              v-if="isEditingCell(-1, col)"
              ref="input"
              :value="editing.text"
              class="studio-sheet-input w-full bg-white font-medium text-gray-900 outline-none"
              :aria-label="column.label"
              @input="onInput"
              @keydown="onEditKeydown"
              @blur="finishEditing(true)"
              @mousedown.stop
            />
            <span v-else class="block truncate">{{ column.header }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in rows" :key="row.key" role="row" :aria-rowindex="rowIndex + 2">
          <th class="sticky left-0 z-10 w-9 border-b border-r border-gray-200 bg-gray-50 px-1 text-right font-normal text-gray-400 tabular-nums" scope="row" role="rowheader">{{ row.header ?? rowIndex + 1 }}</th>
          <td
            v-for="(cell, col) in row.cells"
            :id="cellId(rowIndex, col)"
            :key="columns[col].key"
            :role="columns[col].leading ? 'rowheader' : 'gridcell'"
            :aria-colindex="col + 2"
            :aria-selected="(active.row === rowIndex && active.col === col) || inRange(rowIndex, col)"
            :aria-invalid="cell.invalid !== undefined && cell.invalid !== null ? 'true' : undefined"
            :aria-label="ariaLabelOf(rowIndex, col)"
            :title="cell.title"
            class="studio-sheet-cell h-7 border-b border-r border-gray-200 px-2 whitespace-nowrap"
            :class="{
              'bg-gray-50/70 text-gray-700': columns[col].leading,
              'text-right tabular-nums': cell.numeric ?? isNumeric(col),
              'is-active': active.row === rowIndex && active.col === col,
              'is-range': inRange(rowIndex, col),
              'is-invalid': cell.invalid !== undefined && cell.invalid !== null,
              'is-continued': cell.group === 'cont',
              'text-gray-400': cell.readonly,
            }"
            :data-testid="`studio-sheet-cell-${rowIndex}-${col}`"
            @mousedown="onCellMousedown($event, rowIndex, col)"
            @mouseenter="onCellMouseenter(rowIndex, col)"
            @dblclick="onCellDblclick(rowIndex, col)"
          >
            <input
              v-if="isEditingCell(rowIndex, col)"
              ref="input"
              :value="editing.text"
              class="studio-sheet-input w-full bg-white text-gray-900 outline-none"
              :class="{ 'text-right': cell.numeric ?? isNumeric(col) }"
              :aria-label="ariaLabelOf(rowIndex, col)"
              inputmode="text"
              @input="onInput"
              @keydown="onEditKeydown"
              @blur="finishEditing(true)"
              @mousedown.stop
            />
            <span v-else-if="cell.invalid !== undefined && cell.invalid !== null" class="block truncate text-red-700" :title="cell.invalidMessage">{{ cell.invalid }}</span>
            <span v-else class="block truncate">{{ cell.display }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.studio-sheet-cell.is-active {
  box-shadow: inset 0 0 0 2px var(--p-primary-500);
}
.studio-sheet-cell.is-range {
  background-color: var(--p-primary-50);
}
.studio-sheet-cell.is-invalid {
  background-color: #fef2f2;
  box-shadow: inset 0 -2px 0 #dc2626;
}
.studio-sheet-cell.is-invalid.is-active {
  box-shadow:
    inset 0 0 0 2px var(--p-primary-500),
    inset 0 -2px 0 #dc2626;
}
.studio-sheet-cell.is-continued {
  border-top-color: transparent;
}
tbody tr td.is-continued {
  color: transparent;
}
.studio-sheet-input {
  font: inherit;
  padding: 0;
  border: 0;
}
</style>
